import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PARTNER } from "@/config/partner";

const DISMISS_KEY = "rainbet_link_prompt_dismissed";

/**
 * Prompts logged-in users who have not linked their Rainbet username + ID.
 * Dismissable for the current session.
 */
export const RainbetLinkPrompt = () => {
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [username, setUsername] = useState("");
  const [rainbetId, setRainbetId] = useState("");
  const [missingName, setMissingName] = useState(false);
  const [missingId, setMissingId] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const check = async (uid: string | null) => {
      if (!uid) return;
      if (sessionStorage.getItem(DISMISS_KEY) === uid) return;

      const { data } = await supabase
        .from("profiles")
        .select("gamdom_name, rainbet_id, onboarding_completed")
        .eq("id", uid)
        .maybeSingle();

      if (!data) return;

      const noName = !((data as any).gamdom_name ?? "").trim();
      const noId = !((data as any).rainbet_id ?? "").trim();
      if (!noName && !noId) return;

      setUserId(uid);
      setMissingName(noName);
      setMissingId(noId);
      setOpen(true);
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      check(session?.user?.id ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      check(session?.user?.id ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const dismiss = () => {
    if (userId) sessionStorage.setItem(DISMISS_KEY, userId);
    setOpen(false);
  };

  const handleSave = async () => {
    if (!userId) return;

    const updates: { gamdom_name?: string; rainbet_id?: string } = {};
    if (missingName) {
      const v = username.trim();
      if (!v) {
        toast.error(`Enter your ${PARTNER.name} username`);
        return;
      }
      updates.gamdom_name = v;
    }
    if (missingId) {
      const v = rainbetId.trim();
      if (!/^[A-Za-z0-9-]{3,64}$/.test(v)) {
        toast.error(`Enter a valid ${PARTNER.name} ID (letters, numbers and dashes)`);
        return;
      }
      updates.rainbet_id = v;
    }

    setSaving(true);
    try {
      if (updates.rainbet_id) {
        const { data: existing } = await supabase
          .from("profiles")
          .select("id")
          .eq("rainbet_id", updates.rainbet_id)
          .neq("id", userId)
          .maybeSingle();
        if (existing) {
          toast.error("That Rainbet ID is already linked to another account");
          return;
        }
      }

      const { error } = await supabase.from("profiles").update(updates).eq("id", userId);
      if (error) {
        toast.error(error.message);
        return;
      }

      toast.success(`${PARTNER.name} details saved`);
      if (userId) sessionStorage.setItem(DISMISS_KEY, userId);
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? dismiss() : setOpen(o))}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-wide">
            Link your {PARTNER.name} account
          </DialogTitle>
          <DialogDescription>
            We need your {PARTNER.name} username and ID to track your wager for monthly
            milestones and the leaderboard. These are locked once saved.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {missingName && (
            <div className="space-y-2">
              <Label htmlFor="prompt-rainbet-name">{PARTNER.name} Username</Label>
              <Input
                id="prompt-rainbet-name"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={`Your ${PARTNER.name} username`}
              />
            </div>
          )}
          {missingId && (
            <div className="space-y-2">
              <Label htmlFor="prompt-rainbet-id">{PARTNER.name} ID</Label>
              <Input
                id="prompt-rainbet-id"
                value={rainbetId}
                onChange={(e) => setRainbetId(e.target.value)}
                placeholder={`Your ${PARTNER.name} ID`}
              />
              <p className="text-xs text-muted-foreground">
                Find this in your {PARTNER.name} account settings.
              </p>
            </div>
          )}
        </div>

        <div className="rounded-lg border border-border/60 bg-muted/30 p-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground">
            Don't have an account yet? You can create it here.
          </p>
          <Button
            asChild
            size="sm"
            className="bg-secondary text-secondary-foreground hover:bg-secondary/90 shrink-0"
          >
            <a href={PARTNER.affiliateUrl} target="_blank" rel="noopener noreferrer">
              Sign up on {PARTNER.name}
            </a>
          </Button>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={dismiss} disabled={saving}>
            Later
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RainbetLinkPrompt;